/**
 * Pure utility functions for CSV import parsing.
 *
 * Extracted from CsvImportModal.tsx to reduce function complexity.
 */

import type { ImportEndpointItem } from '../../../types/case-container'
import { VALID_DEVICE_TYPES } from './helpers'

export interface BlockingError {
  row: number
  field: string
  value: string
  message: string
}

export interface FixableWarning {
  row: number
  field: string
  originalValue: string
  suggestedValue: string
  message: string
}

export interface CsvParseResult {
  parsed: ImportEndpointItem[]
  preview: ImportEndpointItem[]
  blockingErrors: BlockingError[]
  fixableWarnings: FixableWarning[]
}

const PREVIEW_ROW_LIMIT = 10

export const cleanHeaderName = (header: string): string =>
  header
    .trim()
    .toLowerCase()
    .replaceAll(/\s*\(required\)\s*/gi, '')
    .replaceAll(/\s*\(optional\)\s*/gi, '')
    .replaceAll(/\s+/g, '_')

export const parseCsvLine = (line: string): string[] => {
  const fields: string[] = []
  let current = ''
  let inQuotes = false

  for (const char of line) {
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      fields.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  fields.push(current.trim())
  return fields
}

const DEVICE_TYPE_KEYWORDS: ReadonlyArray<readonly [readonly string[], string]> = [
  [['laptop', 'notebook'], 'laptop'],
  [['desktop', 'pc'], 'workstation'],
  [['server'], 'server'],
  [['router'], 'router'],
  [['switch'], 'switch'],
  [['firewall'], 'firewall'],
  [['vm', 'virtual'], 'virtual_machine'],
  [['container', 'docker'], 'container'],
  [['cloud', 'aws', 'azure'], 'cloud_instance'],
]

export const getSuggestedDeviceType = (deviceType: string): string | null => {
  const normalised = deviceType.toLowerCase().trim()
  for (const [keywords, suggestion] of DEVICE_TYPE_KEYWORDS) {
    if (keywords.some((kw) => normalised.includes(kw))) return suggestion
  }
  return null
}

interface ColumnIndices {
  hostnameIndex: number
  typeIndex: number
  ipIndex: number
  domainIndex: number
  osIndex: number
}

function resolveColumnIndices(headers: string[]): ColumnIndices {
  return {
    hostnameIndex: headers.findIndex(
      (name) => name === 'hostname' || name === 'name' || name === 'host'
    ),
    typeIndex: headers.findIndex(
      (name) => name === 'type' || name === 'device_type' || name === 'devicetype'
    ),
    ipIndex: headers.findIndex(
      (name) =>
        name === 'ip' || name === 'ip_address' || name === 'ipaddress' || name === 'ip_addresses'
    ),
    domainIndex: headers.indexOf('domain'),
    osIndex: headers.findIndex(
      (name) => name === 'os' || name === 'operating_system' || name === 'operatingsystem'
    ),
  }
}

function resolveDeviceType(rawType: string): { finalType: string; warning: FixableWarning | null } {
  const normalised = rawType.toLowerCase().trim()
  if (VALID_DEVICE_TYPES.has(normalised)) {
    return { finalType: normalised, warning: null }
  }
  const suggested = getSuggestedDeviceType(rawType) ?? 'unknown'
  return {
    finalType: suggested,
    warning: null,
  }
}

function buildDeviceTypeWarning(args: {
  row: number
  rawType: string
  finalType: string
}): FixableWarning {
  const { row, rawType, finalType } = args
  return {
    row,
    field: 'device_type',
    originalValue: rawType,
    suggestedValue: finalType,
    message: `Unknown device type "${rawType}" will be changed to "${finalType}"`,
  }
}

interface ParseRowArgs {
  values: string[]
  rowNum: number
  indices: ColumnIndices
  existingHostnames: Set<string>
  fileHostnames: Map<string, number[]>
  blocking: BlockingError[]
  fixable: FixableWarning[]
  parsed: ImportEndpointItem[]
  preview: ImportEndpointItem[]
}

function validateHostname(args: {
  hostname: string | undefined
  rowNum: number
  existingHostnames: Set<string>
  fileHostnames: Map<string, number[]>
}): { hostname: string; error: null } | { hostname: null; error: BlockingError } {
  const { hostname, rowNum, existingHostnames, fileHostnames } = args

  if (!hostname) {
    return {
      hostname: null,
      error: {
        row: rowNum,
        field: 'hostname',
        value: '(empty)',
        message: 'Hostname is required but missing',
      },
    }
  }

  const hostnameLower = hostname.toLowerCase()

  if (existingHostnames.has(hostnameLower)) {
    return {
      hostname: null,
      error: {
        row: rowNum,
        field: 'hostname',
        value: hostname,
        message: `Hostname "${hostname}" already exists in the database for this case`,
      },
    }
  }

  const duplicateRows = fileHostnames.get(hostnameLower) ?? []
  if (duplicateRows.length > 1 && duplicateRows[0] !== rowNum) {
    return {
      hostname: null,
      error: {
        row: rowNum,
        field: 'hostname',
        value: hostname,
        message: `Duplicate hostname "${hostname}" - also found in row ${duplicateRows[0]?.toString() ?? '?'}`,
      },
    }
  }

  return { hostname, error: null }
}

function buildEndpointItem(args: {
  hostname: string
  deviceType: string
  values: string[]
  indices: ColumnIndices
}): ImportEndpointItem {
  const { hostname, deviceType, values, indices } = args
  const ipValue = indices.ipIndex !== -1 ? values[indices.ipIndex]?.trim() : ''
  const domainValue = indices.domainIndex !== -1 ? values[indices.domainIndex]?.trim() : ''
  const osValue = indices.osIndex !== -1 ? values[indices.osIndex]?.trim() : ''
  const item: ImportEndpointItem = { hostname, deviceType }
  if (ipValue) item.ipAddresses = ipValue.split('|').map((ip) => ip.trim())
  if (domainValue) item.domain = domainValue
  if (osValue) item.operatingSystem = osValue
  return item
}

function parseRow(args: ParseRowArgs): void {
  const {
    values,
    rowNum,
    indices,
    existingHostnames,
    fileHostnames,
    blocking,
    fixable,
    parsed,
    preview,
  } = args
  const validation = validateHostname({
    hostname: values[indices.hostnameIndex]?.trim(),
    rowNum,
    existingHostnames,
    fileHostnames,
  })
  if (validation.error) {
    blocking.push(validation.error)
    return
  }

  const rawType =
    indices.typeIndex !== -1 ? (values[indices.typeIndex]?.trim() ?? 'workstation') : 'workstation'
  const { finalType } = resolveDeviceType(rawType)
  if (!VALID_DEVICE_TYPES.has(rawType.toLowerCase().trim())) {
    fixable.push(buildDeviceTypeWarning({ row: rowNum, rawType, finalType }))
  }

  parsed.push(
    buildEndpointItem({ hostname: validation.hostname, deviceType: finalType, values, indices })
  )

  if (preview.length < PREVIEW_ROW_LIMIT) {
    preview.push(
      buildEndpointItem({ hostname: validation.hostname, deviceType: rawType, values, indices })
    )
  }
}

function buildHostnameMap(dataLines: string[], hostnameIndex: number): Map<string, number[]> {
  const fileHostnames = new Map<string, number[]>()
  for (let i = 1; i < dataLines.length; i++) {
    const line = dataLines[i]
    if (!line) continue
    const hostname = parseCsvLine(line)[hostnameIndex]?.trim().toLowerCase()
    if (!hostname) continue
    const rows = fileHostnames.get(hostname) ?? []
    rows.push(i + 1)
    fileHostnames.set(hostname, rows)
  }
  return fileHostnames
}

function validateCsvStructure(args: {
  dataLines: string[]
  fileName: string
}): { headers: string[]; indices: ColumnIndices } | { fatalError: BlockingError } {
  const { dataLines, fileName } = args
  if (dataLines.length < 2) {
    return {
      fatalError: {
        row: 0,
        field: 'file',
        value: fileName,
        message: 'CSV file must have a header row and at least one data row',
      },
    }
  }
  const headerLine = dataLines[0]
  if (!headerLine) {
    return {
      fatalError: {
        row: 0,
        field: 'file',
        value: fileName,
        message: 'CSV file must have a header row',
      },
    }
  }
  const headers = parseCsvLine(headerLine).map((header) => cleanHeaderName(header))
  const indices = resolveColumnIndices(headers)
  if (indices.hostnameIndex === -1) {
    return {
      fatalError: {
        row: 1,
        field: 'header',
        value: headers.join(', '),
        message: 'CSV must have a hostname column (hostname, name, or host)',
      },
    }
  }
  return { headers, indices }
}

export function parseCsvContent(args: {
  text: string
  fileName: string
  existingHostnames: Set<string>
}): CsvParseResult | { fatalError: BlockingError } {
  const { text, fileName, existingHostnames } = args
  const dataLines = text
    .split('\n')
    .filter((line) => line.trim())
    .filter((line) => !line.trim().startsWith('#'))

  const structure = validateCsvStructure({ dataLines, fileName })
  if ('fatalError' in structure) return structure
  const { indices } = structure

  const fileHostnames = buildHostnameMap(dataLines, indices.hostnameIndex)
  const parsed: ImportEndpointItem[] = []
  const preview: ImportEndpointItem[] = []
  const blockingErrors: BlockingError[] = []
  const fixableWarnings: FixableWarning[] = []

  for (let i = 1; i < dataLines.length; i++) {
    const line = dataLines[i]
    if (!line) continue
    parseRow({
      values: parseCsvLine(line),
      rowNum: i + 1,
      indices,
      existingHostnames,
      fileHostnames,
      blocking: blockingErrors,
      fixable: fixableWarnings,
      parsed,
      preview,
    })
  }

  return { parsed, preview, blockingErrors, fixableWarnings }
}
